/* eslint-disable no-mixed-spaces-and-tabs */
/* eslint-disable simple-import-sort/imports */
import { Box, Divider } from "@mui/material";
import { format } from "date-fns";
import React from "react";
import { useTranslation } from "react-i18next";
import ExportButton from "../../../../design/export-button";
import ColumnSelector from "../../../../design/column-selector";
import Searchbar from "../../../../design/search-bar";
import useViewport from "../../../../utils/use-viewport";
import AppliedFilters from "../../../filters/applied-filters";
import CrossSkillingDashboardSummaries from "./cross-skilling-dashboard-summaries";
import {
	InterestedWorkersCount,
	Location,
	SkillInsightModel,
	SkillLevel,
	WorkerInsightModel, columnType,
} from "../../../../components/insight-dashboard/slices/insight-report-types";

interface CrossSkillingDashboardHeaderProps {
	isSkillPriorityEnabled: boolean;
	jobSkillLevelMapping: boolean;
	allPriorities: any[];
	allLocations: Location[];
	allSkillLevels: SkillLevel[];
	selectedLocations: number[];
	setSearchText: React.Dispatch<React.SetStateAction<string>>;
	searchText: string;
	overAllColumns: columnType[];
	setOverAllColumns: React.Dispatch<React.SetStateAction<columnType[]>>;
	fetchWorkerInsightReportStatus: "idle" | "pending" | "fulfilled" | "error";
	exportData: any;
	fetchSkillInsightReportStatus: "idle" | "pending" | "fulfilled" | "error";
	noOfWorkers: number;
	noOfSkills: number;
	selectedKPI: string;
	setKpi: React.Dispatch<React.SetStateAction<string>>;
	skillsWithLessThanXWorkers: SkillInsightModel[];
	workersWithLessThenXSkills: WorkerInsightModel[];
	kpiCount: any;
}

const CrossSkillingDashboardDataHeader: React.FC<
	CrossSkillingDashboardHeaderProps
> = ({
	isSkillPriorityEnabled,
	jobSkillLevelMapping,
	allPriorities,
	allLocations,
	allSkillLevels,
	selectedLocations,
	setSearchText,
	searchText,
	overAllColumns,
	setOverAllColumns,
	fetchWorkerInsightReportStatus,
	exportData,
	fetchSkillInsightReportStatus,
	selectedKPI,
	setKpi,
	skillsWithLessThanXWorkers,
	workersWithLessThenXSkills,
	noOfWorkers,
	noOfSkills,
	kpiCount,
}) => {
	const { t } = useTranslation();
	const { width }: any = useViewport();

	const locationColumnEnabled = overAllColumns.find(
		(item: columnType) =>
			item.field === "workerLocation" && item.checked === true
	);
	const workcenterColumnEnabled = overAllColumns.find(
		(item: columnType) => item.field === "workcenterName" && item.checked === true
	);

	const primaryJobColumnEnabled = overAllColumns.find(
		(item: columnType) => item.field === "primaryJobName" && item.checked === true
	);

	const islocationColumnEnabled = !!locationColumnEnabled;
	const isworkcenterColumnEnabled = !!workcenterColumnEnabled;
	const isprimaryJobColumnEnabled = !!primaryJobColumnEnabled;

	const exportFilteredData = () => {
		if (
			selectedKPI !== "skillsWithLessThenXWorkersKPI" &&
			selectedKPI !== "interestedWorkersKPI"
		) {
			return exportWorkerInsight();
		} else {
			return exportSkillInsights();
		}
	};

	const exportWorkerInsight = () => {
		const template = exportData.map((item: any) => {
			const workerSkillsAtEachLevel: any = {};

			let totalSkillsCount = 0;
			if (!isSkillPriorityEnabled) {
				for (let i = 0; i < allSkillLevels.length; i++) {
					const skill = item.workerSkillCountByLevel?.find(
						(x: any) => x.level === allSkillLevels[i].level
					);
					workerSkillsAtEachLevel[` ${allSkillLevels[i].name} `] = skill
						? skill.count
						: "";
				}
			}
			item.workerSkillCountByLevel?.forEach((skill: any) => {
				const level = allSkillLevels.find((x) => x.level === skill.level);
				if (level && level.isQualifiedAsSkilled) {
					if (jobSkillLevelMapping) {
						const minRequiredLevel = allSkillLevels.find(
							(x: { id: number }) => x.id === skill.minSkillLevelId
						);
						if (minRequiredLevel && minRequiredLevel.level <= skill.level) {
							totalSkillsCount += skill.count;
						}
					} else {
						totalSkillsCount += skill.count;
					}
				}
			});

			const prs =
				isSkillPriorityEnabled &&
				allPriorities.reduce((acc, val) => {
					acc[val.name] = item.workerSkillCountByPriority.find(
						(x: any) => x.priorityId === val.id
					)
						? item.workerSkillCountByPriority.find(
								(x: any) => x.priorityId === val.id
						  ).count
						: 0;
					return acc;
				}, {});

			const totalSkillScore = isSkillPriorityEnabled && {
				[t("cross-skilling-dashboard_total-skill-score")]: item.skillScore,
			};

			const requiredSkills = jobSkillLevelMapping && {
				[t("cross-skilling-dashboard_required-skills")]: item.requiredSkillsCount,
			};

			const  skillDemonstrated = jobSkillLevelMapping && {
				[t("cross-skilling-dashboard_skills-demonstrated")]:
					item.workerSkillsDemostratedCount,
			};

			const skillsGap = jobSkillLevelMapping && {
				[t("cross-skilling-dashboard_skills-gap")]: item.workerSkillsGapCount,
			};

			const otherSkills = jobSkillLevelMapping && {
				[t("cross-skilling-dashboard_other-skills")]: item.workerOtherSkillsCount,
			};

			const totalSkills = !jobSkillLevelMapping && {
				[t("cross-skilling-dashboard_total-skills")]: totalSkillsCount,
			};

			return {
				[t("cross-skilling-dashboard_worker-name")]:
					item.firstName + " " + item.lastName,
				[t("cross-skilling-dashboard_worker-location")]: item.locationName,
				[t("cross-skilling-dashboard_work-center")]: item.workcenterName,
				[t("cross-skilling-dashboard_primary-job")]: item.primaryJobName,
				...prs,
				[t("cross-skilling-dashboard_joining-date")]: format(
					new Date(item.joiningDate),
					"MM/dd/yyyy"
				),
				...requiredSkills,
				...totalSkills,
				...skillDemonstrated,
				...skillsGap,
				...otherSkills,
				...totalSkillScore,
				[t("cross-skilling-dashboard_interested-skills")]:
					item.interestedSkillsCount,
				[t("cross-skilling-dashboard_worker-backup")]: item.workerBackupCount,
				...workerSkillsAtEachLevel,
			};
		});
		return template;
	};

	//Export data in csv format
	const exportSkillInsights = () => {
		const template = exportData.map((item: any) => {
			const effectiveLocations = selectedLocations.length > 0 
				? selectedLocations 
				: allLocations.map(loc => loc.id);

			const workersAtEachLevel: any = {};

			let workerCoverageCount = 0;

			const workerCountObjects = item.workersSkillLevel
				.filter((x: any) => effectiveLocations.includes(x.locationId)) // Filter by selected or all locations
				.flatMap((x: any) => x.workerCount);

			const aggregatedWorkerCountByLevel = workerCountObjects.reduce((acc: any, curr: any) => {
				acc[curr.level] = (acc[curr.level] || 0) + curr.count;
				return acc;
			}, {});

      if (
        selectedKPI !== "interestedWorkers" &&
        selectedKPI !== "interestedWorkersKPI"
      ) {
        allSkillLevels.forEach((skillLevel) => {
          const count = aggregatedWorkerCountByLevel[skillLevel.level];
          if (count !== undefined) {
            workersAtEachLevel[` ${skillLevel.name} `] = count;

            if (skillLevel.isQualifiedAsSkilled) {
              workerCoverageCount += count;
            }
          } else {
            workersAtEachLevel[` ${skillLevel.name} `] = "";
          }
        });
      }

			const selectedSkillPriority = isSkillPriorityEnabled && allPriorities.find(value =>
				value.id === item?.priorityId);
			const skillPriority = isSkillPriorityEnabled && {
				[t("cross-skilling-dashboard_skill-priority")]: selectedSkillPriority ?
					`${selectedSkillPriority?.code} (${selectedSkillPriority?.name})` : "",
			};

			const getInterestedWorkersCount = (item: { interestedWorkersCount: any }) => {
				try {
					let count = 0;
					const interestedWorkersCount = item.interestedWorkersCount;
					interestedWorkersCount.forEach(
						(interestedWorkerCount: InterestedWorkersCount) => {
							if (selectedLocations && selectedLocations.length) {
								// If filter is applied
								if (selectedLocations.includes(interestedWorkerCount.locationId)) {
									count += interestedWorkerCount.count;
								}
							} else {
								count += interestedWorkerCount.count;
							}
						}
					);
					return count;
				} catch (e) {
					console.error("Get interested worker error", e);
					return 0;
				}
			};

			return {
				[t("cross-skilling-dashboard_skill-name")]: item.skillName,
				...skillPriority,
				[t("cross-skilling-dashboard_interested-workers")]:
					getInterestedWorkersCount(item),
				[t("cross-skilling-dashboard_workers-with-skill")]: workerCoverageCount,
				...workersAtEachLevel,
			};
		});
		return template;
	};

	const handleSelectColumns = (checked: boolean, column: string) => {
		setOverAllColumns(
			overAllColumns.map((item: columnType) => {
				if (item.field === column) {
					return {
						...item,
						checked: !item.checked,
					};
				} else {
					return item;
				}
			})
		);
	};

	return (
		<Box>
			<Box
				display='flex'
				justifyContent='space-between'
				flexDirection={width > 719 ? "row" : "column"}
				sx={{ flex: 1 }}
				alignItems={width > 719 ? "center" : "flex-start"}
        py={1.2}
        px={3}
			>
				<Box>
					<ColumnSelector
						disabledColumns={["workerName"]}
						columns={overAllColumns}
						handleSelectColumns={handleSelectColumns}
					/>
				</Box>
				<Box width={"100%"}>
					<Searchbar
						placeholder={""}
						setSearch={(value: any) =>
							setSearchText(value.replace(/[^a-zA-Z0-9 ]/g, ""))
						}
						value={searchText}
						debounce
					/>
				</Box>
				<Box sx={{ marginLeft: width > 719 ? 3 : 0 }}>
					<ExportButton data={exportFilteredData()} />
				</Box>
			</Box>

			{/* Standard filter pills (Location, Work Center) — between the
			    search row and the KPI cards, matching the worker activity
			    reports layout (divider above the bar, padded KPI section) */}
			<Divider sx={{ mt: 0 }} />
			<AppliedFilters />

			<Box px={2} sx={{ width: "100%", overflow: "hidden" }}>
				<CrossSkillingDashboardSummaries
					fetchWorkerInsightReportStatus={fetchWorkerInsightReportStatus}
					fetchSkillInsightReportStatus={fetchSkillInsightReportStatus}
					skillsWithLessThanXWorkers={skillsWithLessThanXWorkers}
					workersWithLessThenXSkills={workersWithLessThenXSkills}
					noOfWorkers={noOfWorkers}
					noOfSkills={noOfSkills}
					selectedKPI={selectedKPI}
					setKpi={setKpi}
					kpiCount={kpiCount}
				/>
			</Box>
		</Box>
	);
};

export default CrossSkillingDashboardDataHeader;
